import { computed } from "vue"
import { useUnitStore } from "@/stores/unit"
import { useUserStore } from "@/stores/user"

import { truncateString } from "@/utils/utils"
import { formatInTimeZone } from "@/utils/dates"
import { BASE_TIMEZONE } from "@/utils/constants"
import { useThreadsContextStore } from "@/stores/threads"

const MAX_USER_NAME_WITH_LOCATION_LENGTH = 20
const MAX_TIME_LENGTH = 20

export const useThreadDisplay = (threadRef) => {
  const userStore = useUserStore()
  const unitStore = useUnitStore()
  const threadStore = useThreadsContextStore()

  const nameWithLocationDisplay = computed(() => {
    const results = { tooltip: "", title: "" }

    const t = threadRef.value
    if (!t) return results

    const isSelf = t.user_id === userStore.user.id
    const user = userStore.allDict[t.user_id]
      ? userStore.allDict[t.user_id]
      : isSelf
        ? userStore.user
        : {};

    const first = user.first_name?.trim()
    const last = user.last_name?.trim()

    const userName = first || last
      ? `${first || ""} ${last || ""}`.trim()
      : user.email || user.username || "no user name"

    const unitName =
      unitStore.allDict[user.unit_id]?.store_name || "no unit"

    const full = `${userName} (${unitName})`

    results.tooltip = full
    results.title = truncateString(full, MAX_USER_NAME_WITH_LOCATION_LENGTH)
    return results
  })

  const timeDisplay = computed(() => {
    const results = { tooltip: "", title: "" }

    const t = threadRef.value
    if (!t) return results

    const createdAt = t.dt_ticket_open || t.updated_at
    const formatted = formatInTimeZone(
      createdAt,
      BASE_TIMEZONE,
      "hh:mm A, DD MMM YYYY"
    )

    results.tooltip = formatted || ""
    results.title = truncateString(formatted, MAX_TIME_LENGTH)
    return results
  })

  const threadCardActions = computed(() => {
    const t = threadRef.value
    if (!t) return []

    const userId = userStore.authUserId;

    const seenBy = t.seen_by || []
    const assignees = t.assignees || []

    const isSeen = seenBy.includes(userId) || !assignees.includes(userId);
    return [
      {
        label: isSeen ? "Mark as unread" : "Mark as read",
        onClick: async () => {
          const userId = userStore.authUserId

          const newSeenBy = isSeen
            ? seenBy.filter(id => id !== userId)
            : [...seenBy, userId]

          await threadStore.patchThread(t._id, {
            seen_by: newSeenBy,
          })
        },
      }
    ]
  })

  const titleRef = ref(threadRef.value.user_input_summary?.title || threadRef.value.thread_summary?.title)
  const summaryRef = ref(threadRef.value.user_input_summary?.summary || threadRef.value.thread_summary?.summary)

  const setTitle = (data) => {
    titleRef.value = data
  }

  const setSummary = (data) => {
    summaryRef.value = data
  }

  watch(threadRef, (newThread) => {
    setTitle(newThread.user_input_summary?.title || newThread.thread_summary?.title)
    setSummary(newThread.user_input_summary?.summary || newThread.thread_summary?.summary)
  })

  return {
    nameWithLocationDisplay,
    timeDisplay,
    threadCardActions,
    titleRef,
    summaryRef,
  }
}
